import { useId, type KeyboardEvent, type ReactElement } from 'react';

import styles from './SegmentedControl.module.css';

export interface SegmentOption<T extends string> {
    value: T;
    label: string;
}

export function SegmentedControl<T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: readonly SegmentOption<T>[];
    onChange: (value: T) => void;
}): ReactElement {
    const labelId = useId();
    function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
        const index = options.findIndex((option) => option.value === value);
        if (index < 0) {
            return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            const next = options[(index + 1) % options.length];
            if (next !== undefined) onChange(next.value);
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            const previous = options[(index - 1 + options.length) % options.length];
            if (previous !== undefined) onChange(previous.value);
        }
    }

    return (
        <div>
            <p className={styles.legend} id={labelId}>
                {label}
            </p>
            <div
                className={styles.group}
                role="radiogroup"
                aria-labelledby={labelId}
                onKeyDown={onKeyDown}
            >
                {options.map((option) => {
                    const selected = option.value === value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            className={
                                selected ? `${styles.option} ${styles.selected}` : styles.option
                            }
                            aria-checked={selected}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => {
                                onChange(option.value);
                            }}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
